import React, { FC } from 'react';
import WarningBox from 'components/Elements/WarningBox';
import Checkbox from 'components/Elements/Checkbox';
import CheckboxSelector from 'components/Elements/CheckboxSelector';
import FormSection from '../FormSection';
import './siteAccessNotifications.scss';

interface SiteAccessNotificationsProps {
   visitors: {
        id: number;
        name: string;
        email?: string;
    }[];
    selectedVisitors: number[];
    onSelect: (values: number[]) => void;
}

const SiteAccessNotifications: FC<SiteAccessNotificationsProps> = ({ visitors, selectedVisitors, onSelect }) => {
    // Email is optional for manually added visitors, so anyone without one
    // can't be notified and is left out of the selection entirely.
    const notifiableVisitors = visitors.filter((visitor) => !!visitor.email && visitor.email.trim() !== '');
    const notifiableIds = notifiableVisitors.map((visitor) => visitor.id);
    const checkboxOptions = notifiableVisitors.sort((a, b) => a.name.localeCompare(b.name)).map((visitor) => ({ label: visitor.name, value: visitor.id.toString() }));

    // "Notify all" is derived from the selection rather than held as local
    // state, so a parent-side change (form reset, step reload) can't leave the
    // toggle disagreeing with the visitors actually selected.
    const notifyAll = notifiableIds.length > 0 && notifiableIds.every((id) => selectedVisitors.includes(id));

    const handleCheckboxClick = () => {
        onSelect(notifyAll ? [] : notifiableIds);
    };

    const handleVisitorSelect = (values: string[]) => {
        onSelect(values.map((value) => parseInt(value, 10)));
    };

    return (
        <FormSection title="Notifications">
            <div className="sa-notifications-container">
                <div className="warning-wrapper">
                    <WarningBox>If you tick this box, all engineers listed as a visitor will be notified about the request including the reference and approval statuses throughout the process.</WarningBox>
                </div>
                <div className="checkbox-wrapper">
                    <Checkbox checked={notifyAll} onClick={handleCheckboxClick} />
                    <span className="label">Notify all visitors via email of request</span>
                </div>
                {/* Kept visible even when every visitor is selected: now that
                    "notify all" is derived, hiding it would make the list
                    disappear the moment the user ticks the last visitor. */}
                <div className="select-visitors-container">
                    <h5 className="heading">Select Visitors</h5>
                    <CheckboxSelector
                        options={checkboxOptions}
                        values={selectedVisitors.map((visitor) => visitor.toString())}
                        onChange={handleVisitorSelect} />
                </div>
            </div>
        </FormSection>
    );
};

export default SiteAccessNotifications;
